'use client';

import type { ButtonHTMLAttributes, FC, ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { Spinner } from './Spinner';
import './Button.css';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'destructive'
    | 'destructiveSecondary'
    | 'destructiveTertiary'
    | 'success'
    | 'successSecondary'
    | 'black'
    | 'share'
    | 'shareBigWin'
    | 'cta'
    | 'ctaPurple';
  mode?: 'primary' | 'secondary' | 'tertiary' | 'black' | 'success';
  size?: 's' | 'm' | 'l';
  stretched?: boolean;
  loading?: boolean;
  before?: ReactNode;
  after?: ReactNode;
  iconPosition?: 'left' | 'top';
}

// Mirrors Apple iOS 26 "Button - Content Area" (Figma Buttons page).
// Three base styles × two destructive states = six core variants:
//   primary              → Style=Bordered-Prominent          (accent-blue fill, white text)
//   destructive          → Style=Bordered-Prominent, Dest=Y  (accent-red  fill, white text)
//   secondary            → Style=Bordered                    (fill-tertiary, accent-blue text)
//   destructiveSecondary → Style=Bordered,           Dest=Y  (fill-tertiary, accent-red  text)
//   tertiary             → Style=Bordered - Secondary        (fill-tertiary, label-primary text)
//   destructiveTertiary  → Style=Bordered - Secondary, Dest=Y(fill-tertiary, accent-red  text)
// Sizes match Figma:
//   Small  — px-[10px] py-[4px], 15/20, tracking -0.23
//   Medium — px-[14px] py-[7px], 15/20, tracking -0.23
//   Large  — px-[20px] py-[14px], 17/22, tracking -0.43
// Borderless / "ghost" intentionally omitted.
// share/shareBigWin/cta/ctaPurple keep custom gradients (not in Apple HIG).
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 border-none rounded-full font-normal cursor-pointer transition-colors duration-200 ease-smooth relative overflow-hidden outline-none whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-accent-blue text-white',
        secondary: 'bg-bg-secondary text-accent-blue',
        tertiary: 'bg-bg-secondary text-label-primary',
        destructive: 'bg-accent-red text-white',
        destructiveSecondary: 'bg-bg-secondary text-accent-red',
        destructiveTertiary: 'bg-bg-secondary text-accent-red',
        black: 'bg-black text-white',
        success: 'bg-accent-green text-white',
        successSecondary: 'bg-bg-secondary text-accent-green',
        share: 'bg-gradient-to-r from-[#6C5CE7] to-[#a855f7] text-white',
        shareBigWin: 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black shadow-[0_0_20px_rgba(255,215,0,0.25)]',
        cta: 'bg-accent-blue text-white relative overflow-hidden before:absolute before:inset-0 before:bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] before:bg-[length:200%_100%] before:animate-[ctaShimmer_4s_ease-in-out_infinite]',
        ctaPurple: 'bg-[linear-gradient(135deg,#a855f7_0%,#ec4899_100%)] text-white relative overflow-hidden before:absolute before:inset-0 before:bg-[linear-gradient(110deg,transparent_25%,rgba(255,255,255,0.3)_50%,transparent_75%)] before:bg-[length:200%_100%] before:animate-[ctaShimmer_4s_ease-in-out_infinite]',
      },
      size: {
        s: 'px-[10px] py-[4px] text-[15px] leading-[20px] tracking-[-0.23px]',
        m: 'px-[14px] py-[7px] text-[15px] leading-[20px] tracking-[-0.23px]',
        l: 'px-[20px] py-[14px] text-[17px] leading-[22px] tracking-[-0.43px]',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'm',
    },
  },
);

export const Button: FC<ButtonProps> = ({
  children,
  variant = 'primary',
  mode,
  size = 'm',
  stretched = false,
  loading = false,
  disabled,
  before,
  after,
  iconPosition = 'left',
  className,
  ...props
}) => {
  const variantClass = mode ? mode : variant;
  const isIconTop = iconPosition === 'top';

  return (
    <button
      className={cn(
        buttonVariants({ variant: variantClass, size }),
        stretched && 'w-full',
        isIconTop && 'flex-col gap-1',
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <Spinner size="s" color="white" />
      ) : (
        <>
          {before && (
            <span className={cn('flex items-center', !isIconTop && 'mr-2')}>{before}</span>
          )}
          {children}
          {after && (
            <span className={cn('flex items-center', !isIconTop && 'ml-2')}>{after}</span>
          )}
        </>
      )}
    </button>
  );
};
